import type React from "react";
import { useTranslation } from "react-i18next";
import { CommentActionButton } from "@/components/common/CommentActionButton";
import { ReactionButton } from "@/components/common/ReactionButton";
import { useReactionFooterState } from "@/components/common/hooks/useReactionFooterState";
import { useReactionPopupTrigger } from "@/components/common/hooks/useReactionPopupTrigger";
import { ReactionSummary } from "@/components/common/ReactionSummary";
import type {
  Reaction,
  ReactionBreakdown,
  ReactionType,
} from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";

interface ReactionFooterProps {
  localReaction: ReactionType | null;
  onReactionSelect: (reaction: ReactionType) => void | Promise<void>;
  isReacting?: boolean;
  isPending?: boolean;
  isAnyActionLoading?: boolean;
  disabled?: boolean;
  reactionTotal?: number;
  lastReactions?: Reaction[];
  reactionBreakdown?: ReactionBreakdown;
  reactionContentId?: number | string | null;
  reactionObjectModel?: string;
  reactionObjectId?: number | string | null;
  showCommentButton?: boolean;
  onCommentClick?: () => void;
  isCommentActive?: boolean;
  className?: string;
  dividerClassName?: string;
  reactionInactiveIconOpacityClass?: string;
}

export const ReactionFooter: React.FC<ReactionFooterProps> = ({
  localReaction,
  onReactionSelect,
  isReacting = false,
  isPending = false,
  isAnyActionLoading = false,
  disabled = false,
  reactionTotal = 0,
  lastReactions = [],
  reactionBreakdown,
  reactionContentId,
  reactionObjectModel,
  reactionObjectId,
  showCommentButton = true,
  onCommentClick,
  isCommentActive = false,
  className,
  dividerClassName,
  reactionInactiveIconOpacityClass,
}) => {
  const { t } = useTranslation();
  const {
    reactionRefreshKey,
    isActionDisabled,
    reactionSummaryShouldRender,
    bumpReactionRefreshKey,
  } = useReactionFooterState({
    isAnyActionLoading,
    disabled,
    reactionTotal,
    hasReactionTarget: Boolean(reactionContentId || reactionObjectId),
  });
  const isReactionActionDisabled = isReacting || isPending || isActionDisabled;
  const reactionPopupTrigger = useReactionPopupTrigger({
    isActionDisabled: isReactionActionDisabled,
    localReaction,
    onReactionSelect,
    onAfterReaction: bumpReactionRefreshKey,
  });
  const reactionText = `${t("common.reaction")}${reactionTotal > 0 ? ` (${reactionTotal})` : ""}`;
  const commentText = t("common.comment");
  const isCommentDisabled = isPending || isActionDisabled || !onCommentClick;
  const handleCommentButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    onCommentClick?.();
  };

  return (
    <footer className={cn("flex flex-col gap-0", dividerClassName, className)}>
      <div className="flex items-center gap-1">
        {reactionSummaryShouldRender && (
          <ReactionSummary
            contentId={reactionContentId}
            objectModel={reactionObjectModel}
            objectId={reactionObjectId}
            total={reactionTotal}
            lastReactions={lastReactions}
            reactionBreakdown={reactionBreakdown}
            currentUserEmotion={localReaction}
            refreshKey={reactionRefreshKey}
            className="gap-1"
          />
        )}
        <ReactionButton
          localReaction={localReaction}
          reactionText={reactionText}
          showReactionLabel={false}
          inactiveReactionIconOpacityClass={
            reactionInactiveIconOpacityClass ?? "opacity-70"
          }
          size="sm"
          isPending={isPending}
          isActionDisabled={isReactionActionDisabled}
          showReactions={reactionPopupTrigger.showReactions}
          onMouseEnter={reactionPopupTrigger.onMouseEnter}
          onMouseLeave={reactionPopupTrigger.onMouseLeave}
          onClick={reactionPopupTrigger.onClick}
          onReactionSelect={reactionPopupTrigger.onReactionSelect}
          onReactionClear={reactionPopupTrigger.onReactionClear}
        />
      </div>

      <div className="flex items-center justify-between bg-transparent">
        {showCommentButton && (
          <CommentActionButton
            commentText={commentText}
            onClick={handleCommentButtonClick}
            isCommentDisabled={isCommentDisabled}
            isCommentActive={isCommentActive}
            size="sm"
          />
        )}
      </div>
    </footer>
  );
};
